import * as storage from './storage';
import { Auth } from '@aws-amplify/auth';
import toast, { toaster, styles } from './display.toast';
import { progress } from './display.progress';
import Vue from 'vue';
import Router from 'vue-router';
import combine from 'url-join';
import { config, isLoginRedirect, failure, success, signin, initialRoute, signInEvent, isMasqUrl } from './authn.config';
import { getParameterByName } from './parse.url';
import EventEmitter from 'events';
const wait = __GMK_COGNITO_WAIT_TO_MOUNT__ || false;
const donebus = new EventEmitter();
/** previous config */
const previous = (async () => {
    const state = getParameterByName('state');
    if (!state) {
        return undefined;
    }
    try {
        return await storage.canUse && await storage.instance.getItem(state);
    }
    catch (_a) {
        return undefined;
    }
})();
/**
 * Helper to identify if the user has already logged in.
 * @returns {boolean} if the user is already logged in
 */
const isUserSignedIn = async () => {
    try {
        await signInEvent;
        const session = await Auth.currentSession();
        __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): session valid`, session);
        return session.isValid();
    }
    catch (_a) {
        return false;
    }
};
/** process callback logic sequentially */
isLoginRedirect(initialRoute, config) && (async () => {
    __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): processing login redirect ...`);
    //add display elements
    document.body.appendChild(progress);
    document.head.appendChild(styles);
    document.body.appendChild(toaster);
    //do the login callback stuff async and then wait for the promise
    try {
        const actual = await previous || config;
        Auth.configure(actual);
        //wait for hub to register sign-in event
        const complete = await signInEvent;
        if (!complete) {
            throw 'unsuccessful login';
        }
        //verify user is authenticated
        const isSignedIn = await isUserSignedIn();
        if (!isSignedIn) {
            throw 'invalid session';
        }
        //log success
        __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): successful login`);
        //they are authorized send them back from which they came!
        const url = await (async () => {
            var _a;
            try {
                const stored = await storage.instance.getItem(storage.CURRENT_URL);
                return new URL(stored || '/', (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.origin);
            }
            catch (error) {
                __GMK_COGNITO_VUE_DEBUG__ && console.warn(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): issue getting original url`, error);
                return;
            }
        })();
        //handle url navigation
        try {
            await (success === null || success === void 0 ? void 0 : success(url));
        }
        catch (error) {
            console.warn(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): issue during success logic`, error);
        }
    }
    catch (error) {
        //let the user know
        toast((error === null || error === void 0 ? void 0 : error.message) || error, { persist: true });
        //notify implementation that login failed
        await (failure === null || failure === void 0 ? void 0 : failure(error));
        //log failure
        __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): failed login`, error);
    }
    finally {
        document.body.removeChild(progress);
    }
})();
//stop any further logic because redirect will eventually happen
if (isLoginRedirect(initialRoute, config)) {
    throw `[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): processing login redirect ...`;
}
//do the configuation
__GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): configuring with details`, config);
storage.canUse.then(can => can && storage.instance.setItem(config.userPoolWebClientId, config)); //track config always
Auth.configure(config);
/** Overrides vue router to provide basic configuration */
class GMKCognitoRouterWrapper extends Router {
    init(...params) {
        this.__gmkCognitoRouterInitOverride.apply(this, params); //call original process
        if (this.__gmkCogntioRouterRegistered) {
            return;
        }
        /** Navigation effect for tracking url changes to help after redirect */
        const trackUrl = async (to, from, next) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
            try {
                //determine new URL by replacing
                const willBe = new URL(combine((_a = this === null || this === void 0 ? void 0 : this.history) === null || _a === void 0 ? void 0 : _a.base, to.fullPath), window.location.origin);
                const isEmbeddedRouteMatched = __GMK_COGNITO_IS_MFESHELL__ && (((_e = (_d = (_c = (_b = from === null || from === void 0 ? void 0 : from.matched) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.path) === null || _d === void 0 ? void 0 : _d.indexOf) === null || _e === void 0 ? void 0 : _e.call(_d, '*')) === ((_h = (_g = (_f = from === null || from === void 0 ? void 0 : from.matched) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.path) === null || _h === void 0 ? void 0 : _h.length) - 1 && //coming from embedded
                    ((_m = (_l = (_k = (_j = to.matched) === null || _j === void 0 ? void 0 : _j[0]) === null || _k === void 0 ? void 0 : _k.path) === null || _l === void 0 ? void 0 : _l.indexOf) === null || _m === void 0 ? void 0 : _m.call(_l, '*')) === ((_q = (_p = (_o = to.matched) === null || _o === void 0 ? void 0 : _o[0]) === null || _p === void 0 ? void 0 : _p.path) === null || _q === void 0 ? void 0 : _q.length) - 1 //going to embedded
                );
                const shouldTrack = await storage.canUse;
                const avoidTracking = isMasqUrl(willBe) || isLoginRedirect(willBe, config) || isLoginRedirect(willBe, await previous) || isEmbeddedRouteMatched;
                //do checks in order
                if (avoidTracking) {
                    __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): ignoring url tracking`, this, willBe, from, to);
                    return; //exit early if we are at the login redirect endpoint
                }
                if (shouldTrack) {
                    __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): tracking url navigation`, this, willBe, from, to);
                    await storage.instance.setItem(storage.CURRENT_URL, willBe.href); //track url navigation
                }
            }
            catch (error) {
                console.error(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): issue tracking url`, error, this, from, to);
            }
            finally {
                return await next();
            }
        };
        const checkAuth = async (to, from, next) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            try {
                //determine if we should force auth
                const authFromRoute = ((_a = to.meta) === null || _a === void 0 ? void 0 : _a.auth) || false;
                const isEmbeddedRouteMatched = __GMK_COGNITO_IS_MFESHELL__ && ((_e = (_d = (_c = (_b = to.matched) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.path) === null || _d === void 0 ? void 0 : _d.indexOf) === null || _e === void 0 ? void 0 : _e.call(_d, '*')) === ((_h = (_g = (_f = to.matched) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.path) === null || _h === void 0 ? void 0 : _h.length) - 1;
                const shouldForceAuth = !!authFromRoute && !isEmbeddedRouteMatched;
                const isSignedIn = await isUserSignedIn();
                //do checks in order
                if (!shouldForceAuth) {
                    __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): auth not forced`, this, to);
                    return await next(); //don't care to force user to auth or change auth config
                }
                if (isSignedIn) {
                    __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): cognito identified user as already signed-in`, this, to);
                    return await next(); //user is already signed in (keep them as-is)
                }
                __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): attempting sign-in`, this, to);
                await signin(); //sign-in
            }
            catch (error) {
                console.error(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): issue with auth check`, error, this, from, to);
                return await next();
            }
        };
        this.beforeEach(trackUrl); //add url tracking
        this.beforeEach(checkAuth); //add default guard for sign-in
        this.onReady(async () => {
            var _a;
            //check current initial location
            if (!await storage.canUse && __GMK_COGNITO_VUE_DEBUG__) {
                console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): cannot use storage and cannot track url or handle multiple configurations`);
            }
            const fullPath = window.location.href
                .replace(window.location.origin, '')
                .replace((_a = this === null || this === void 0 ? void 0 : this.history) === null || _a === void 0 ? void 0 : _a.base, '');
            const current = Object.assign(Object.assign({}, this.currentRoute), { fullPath });
            await trackUrl(current, null, () => true); //track users navigation
            await checkAuth(current, null, () => {
                var _a;
                this.__gmkCogntioRouterReady = true; //indicate completion for subsequent mounts
                ((_a = donebus.emit) === null || _a === void 0 ? void 0 : _a.call(donebus, 'router.ready')) || console.warn(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): no .emit('router.ready') events were listening ... expect the unexpected`);
            });
            __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): routing configured`, this, this.currentRoute);
        }, (error) => {
            var _a;
            console.error(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): routing had issues configuring`, this, error);
            this.__gmkCogntioRouterReady = true; //indicate completion for subsequent mounts
            ((_a = donebus.emit) === null || _a === void 0 ? void 0 : _a.call(donebus, 'router.ready')) || console.warn(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): no .emit('router.ready') events were listening ... expect the unexpected`);
        });
        this.__gmkCogntioRouterRegistered = true;
    }
}
;
class GMKCognitoVueWrapper extends Vue {
    $mount(...params) {
        var _a;
        //validations
        if (this.__$gmkCognitoDone) {
            return this.__$gmkCognitoMountOverride.apply(this, params);
        }
        if (!wait) {
            return this.__$gmkCognitoMountOverride.apply(this, params);
        }
        if (!this.$router) {
            console.warn('[@growmark/cognito-vue]: router instance needed and none provided');
            return this.__$gmkCognitoMountOverride.apply(this, params);
        }
        const router = this.$router;
        if (router.__gmkCogntioRouterReady) {
            return this.__$gmkCognitoMountOverride.apply(this, params);
        }
        //add packaged mounting function that will be delayed until the onReady(...) success in vue-router
        __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): delaying Vue.$mount(...) till after VueRouter.onReady(...)`, (_a = this === null || this === void 0 ? void 0 : this.$options) === null || _a === void 0 ? void 0 : _a.name);
        donebus.once('router.ready', () => {
            var _a;
            __GMK_COGNITO_VUE_DEBUG__ && console.debug(`[@growmark/cognito-vue](${__GMK_COGNITO_APP_NAME__}): mounting...`, (_a = this === null || this === void 0 ? void 0 : this.$options) === null || _a === void 0 ? void 0 : _a.name);
            this.__$gmkCognitoDone = true;
            this.__$gmkCognitoMountOverride.apply(this, params);
        });
        return this;
    }
}
;
const V = Vue;
if (!V.prototype.__$gmkCognitoMountOverride) {
    V.prototype.__$gmkCognitoDone = false;
    V.prototype.__$gmkCognitoMountOverride = V.prototype.$mount;
    try {
        V.prototype.$mount = GMKCognitoVueWrapper.prototype.$mount;
    }
    catch (error) {
        console.warn('[@growmark/cognito-vue]: an unexpected issue occurred overwriting vue to override `.$mount(...)', error);
    }
}
const R = Router;
if (!R.prototype.__gmkCognitoRouterInitOverride) {
    R.prototype.__gmkCognitoRouterInitOverride = R.prototype.init;
    R.prototype.__gmkCogntioRouterRegistered = false;
    R.prototype.__gmkCogntioRouterReady = false;
    try {
        R.prototype.init = GMKCognitoRouterWrapper.prototype.init;
    }
    catch (error) {
        console.warn('[@growmark/cognito-vue]: an unexpected issue occurred overwriting vue to override `.init(...)`', error);
    }
}
//# sourceMappingURL=handle.configuration.js.map